import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  StylesProvider,
  useDisclosure,
  useMultiStyleConfig,
  useStyles,
} from '@chakra-ui/react'
import React, { HTMLAttributeAnchorTarget, MouseEvent } from 'react'
import { redirect } from 'react-router'
import { Link } from 'react-router-dom'

import { useAuth } from '../context/AuthContext'
import { redirectToLogout } from '../hooks/useAccount'
import { useApp } from '../hooks/useApp'
import { useOldAppPath } from '../hooks/useOldAppPath'
import {
  Contract,
  Logout,
  News,
  Offer,
  Refresh,
  Settings,
  User,
} from '../icons'
import { MenuItem } from './Menu'

type MobileMenuProps = BoxProps

interface MobileMenuItemProps extends ButtonProps {
  to?: string
  icon?: IconsEnum
  href?: string
  target?: HTMLAttributeAnchorTarget
}

export const enum IconsEnum {
  Offer = 'offer',
  Contract = 'contract',
  Settings = 'settings',
  News = 'news',
  User = 'user',
  Logout = 'logout',
}

const getMenuItemIcon = (icon: IconsEnum) => {
  switch (icon) {
    case 'offer':
      return <Offer />
    case 'contract':
      return <Contract />
    case 'settings':
      return <Settings />
    case 'news':
      return <News />
    case 'user':
      return <User />
    case 'logout':
      return <Logout />
  }
}

export const MobileMenuItem = ({
  children,
  to,
  icon,
  as = Link,
  href,
  target,
  ...rest
}: MobileMenuItemProps) => {
  const styles = useStyles()

  return (
    <Button
      as={href ? 'a' : as}
      target={target}
      href={href}
      to={to}
      {...rest}
      variant="ghost"
      __css={styles.item}
    >
      {icon && (
        <Box sx={{ color: 'secondary.500' }}>{getMenuItemIcon(icon)}</Box>
      )}
      {children}
    </Button>
  )
}

export const MobileMenu = (props: MobileMenuProps) => {
  const { app } = useApp()
  const { children, ...rest } = props
  const {
    isOpen: isOpenSettings,
    onOpen: onOpenSettings,
    onClose: onCloseSettings,
  } = useDisclosure()
  const {
    isOpen: isOpenProfile,
    onOpen: onOpenProfile,
    onClose: onCloseProfile,
  } = useDisclosure()
  const carkulkaUrl = process.env.REACT_APP_CARKULKA_URL ?? ''
  const oldAppLocation = useOldAppPath()

  const { user } = useAuth() ?? {}

  const styles = useMultiStyleConfig('MobileMenu', props)

  const numberOfItems = React.Children.toArray(props.children).length

  const handleSettingsClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    const to = e.currentTarget.getAttribute('to')

    if (isOpenSettings) {
      to && redirect(to)
    }

    onOpenSettings()
  }

  const handleProfileClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    const to = e.currentTarget.getAttribute('to')

    if (isOpenProfile) {
      to && redirect(to)
    }

    onOpenProfile()
  }

  return (
    <Box
      __css={styles.container}
      sx={{ gridTemplateColumns: `repeat(${numberOfItems})` }}
      {...rest}
    >
      <StylesProvider value={styles}>
        {app && (
          <MobileMenuItem to={`/${app}/concepts`} icon={IconsEnum.Offer}>
            Nabídky
          </MobileMenuItem>
        )}
        {app && (
          <MobileMenuItem to={`/${app}/contracts`} icon={IconsEnum.Contract}>
            Smlouvy
          </MobileMenuItem>
        )}

        {((user?.settingsMenu && user.settingsMenu.length > 0) ||
          oldAppLocation) && (
          <MobileMenuItem
            to="/organization/detail"
            onClick={handleSettingsClick}
            icon={IconsEnum.Settings}
          >
            Nastavení
          </MobileMenuItem>
        )}

        {app && (
          <MobileMenuItem to={`/${app}/news`} icon={IconsEnum.News}>
            Aktuality
          </MobileMenuItem>
        )}

        <MobileMenuItem
          // href={`${carkulkaUrl}/Manage/Index`} target='_blank'
          onClick={handleProfileClick}
          icon={IconsEnum.User}
        >
          Profil
        </MobileMenuItem>
      </StylesProvider>

      <Modal isOpen={isOpenSettings} onClose={onCloseSettings}>
        <ModalOverlay />

        <ModalContent sx={{ mx: 4, mt: 'auto' }}>
          <ModalCloseButton />

          <ModalHeader sx={{ px: 4 }}>Nastavení</ModalHeader>

          <ModalBody sx={{ px: 2, pt: 0 }}>
            <Box
              as="ul"
              sx={{
                listStyle: 'none',
                zIndex: 'subMenu',
              }}
            >
              {user &&
                user.settingsMenu!.map((item) => {
                  return (
                    <li key={item.url}>
                      <MenuItem href={item.url}>{item.name}</MenuItem>
                    </li>
                  )
                })}
              {oldAppLocation && (
                <li>
                  <MenuItem
                    variant="outline"
                    colorScheme="gray"
                    href={carkulkaUrl + '/' + oldAppLocation}
                  >
                    <Refresh color="secondary.500" />
                    Přepnout na předchozí verzi aplikace
                  </MenuItem>
                </li>
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenProfile} onClose={onCloseProfile}>
        <ModalOverlay />

        <ModalContent sx={{ mx: 4, mt: 'auto' }}>
          <ModalCloseButton />

          <ModalHeader sx={{ px: 4 }}>{user?.userFullName}</ModalHeader>

          <ModalBody sx={{ px: 2, pt: 0 }}>
            <Box
              as="ul"
              sx={{
                listStyle: 'none',
                zIndex: 'subMenu',
              }}
            >
              {user?.vehicleRights && (
                <li>
                  <MenuItem to="/profile">
                    <User color="secondary.500" />
                    Profil
                  </MenuItem>
                </li>
              )}

              <li>
                <MenuItem onClick={() => redirectToLogout()}>
                  <Logout color="secondary.500" />
                  Odhlásit se
                </MenuItem>
              </li>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  )
}
